import { type KeyboardEvent, forwardRef } from 'react';
import { IoClose } from 'react-icons/io5';
import { abbreviateTag, formatTagLabel } from '@/shared/utils/tagLabel';
import styles from './Tag.module.css';

export type TagVariant = 'default' | 'success' | 'warning';

interface TagProps {
  /** Canonical SCREAMING_SNAKE_CASE value (e.g. "AORTIC_STENOSIS"). */
  value: string;
  /**
   * Visible label. When omitted, derives from `value`:
   *   compact=true  → abbreviateTag(value)  e.g. "AS"
   *   compact=false → formatTagLabel(value) e.g. "Aortic Stenosis"
   */
  label?: string;
  /** Color treatment. */
  variant?: TagVariant;
  /**
   * Abbreviate the auto-derived label. Use for compact / dense surfaces
   * (worklist sidebar, table rows). Worksheet / editable contexts should
   * leave this off so creators always see the full label they typed.
   */
  compact?: boolean;
  /** Show a lock affordance and use the muted "managed elsewhere" treatment. */
  onRemove?: (value: string) => void;
  /**
   * Forwarded to the remove button. Lets a parent handle Backspace on a
   * focused chip without re-implementing the keyboard contract.
   */
  onRemoveKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  /** Hover tooltip. None by default. */
  title?: string;
  /** An AI tag shows a leading ✦, the same mark the measurement rows and AI
   *  statements use. A tag a reader added needs no mark. */
  ai?: boolean;
  /** Optional ARIA role override (e.g. 'listitem' inside a chip row). */
  role?: string;
  /** Optional `data-testid` for the wrapper span. */
  dataTestId?: string;
  /** Optional `data-testid` for the inner remove button. */
  removeDataTestId?: string;
  /** Extra class on the wrapper span (e.g. to retint chips per surface). */
  className?: string;
}

const Tag = forwardRef<HTMLSpanElement, TagProps>(
  (
    {
      value,
      label,
      variant = 'default',
      compact = false,
      onRemove,
      onRemoveKeyDown,
      disabled = false,
      title,
      ai = false,
      role,
      dataTestId,
      removeDataTestId,
      className,
    },
    ref
  ) => {
    const resolvedLabel =
      label ?? (compact ? abbreviateTag(value) : formatTagLabel(value));

    const showRemove = !!onRemove && !disabled;

    const tagClassName = [styles.tag, styles[`variant_${variant}`], className]
      .filter(Boolean)
      .join(' ');

    return (
      <span
        ref={ref}
        role={role}
        className={tagClassName}
        data-testid={dataTestId}
        title={title}
      >
        {ai && (
          <span className={styles.aiMark} role="img" aria-label="Added by AI">
            ✦
          </span>
        )}
        <span className={styles.label}>{resolvedLabel}</span>
        {showRemove && (
          <button
            type="button"
            className={styles.remove}
            data-testid={removeDataTestId}
            onClick={() => onRemove?.(value)}
            onKeyDown={onRemoveKeyDown}
            aria-label={`Remove ${resolvedLabel}`}
          >
            <IoClose size={10} aria-hidden focusable="false" />
          </button>
        )}
      </span>
    );
  }
);

Tag.displayName = 'Tag';

export default Tag;
